import React, { useCallback } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client";
import Loading from "../../components/Loading";
import EmptyListInfo from "../../ui_atoms/EmptyListInfo";
import { StackScreenProps } from "@react-navigation/stack";
import VStack from "../../ui_atoms/VStack";
import { AuditLog } from "../../__generated__/graphql";
import { UserMissionsTabParamList } from "../../navigations/user/UserMissionsNavigation";
import { FIND_MISSION_AUDIT_LOGS } from "../../graphql/core";
import ListItem from "../../ui_atoms/ListItem";
import { useDateTimeFormatter } from "../../hooks/date";
import ScreenCard from "../ScreenCard";
import { usePagination } from "../../hooks/use-pagination";

type MissionLogsScreenProps = StackScreenProps<UserMissionsTabParamList, "MissionLogs">;

const MissionLogsScreen: React.FC<MissionLogsScreenProps> = ({ route }) => {
    const i18n = useI18n();
    const dateFormatter = useDateTimeFormatter();
    const { data, refetch, loading, error, fetchMore } = useQuery(FIND_MISSION_AUDIT_LOGS, {
        variables: {
            missionId: route?.params.missionId || "",
        },
        fetchPolicy: "cache-and-network",
        skip: !route?.params.missionId,
    });

    const count = data?.findMissionAuditLogs?.elements?.length;
    const allLogs = data?.findMissionAuditLogs.elements;

    const renderRow = useCallback(
        (log: AuditLog) => (
            <ListItem
                key={log.id}
                avatarPath={log.userAvatarUrl}
                useBigAvatarOrIcon
                title={log.userDisplayName || ""}
                description={log.text}
                tags={[dateFormatter(new Date(log.createdAt))]}
            />
        ),
        [dateFormatter],
    );

    const pagination = usePagination({
        fetchMore,
    });

    return (
        <ScreenCard
            refresh={refetch}
            pagination={pagination}
            headband={
                error
                    ? {
                          text: error.message,
                          mode: "error",
                      }
                    : undefined
            }
        >
            {loading && !allLogs ? <Loading /> : allLogs?.map(renderRow)}
            {count == 0 && (
                <VStack h={1} v={1}>
                    <EmptyListInfo text={i18n.t("mission.history.empty")} />
                </VStack>
            )}
        </ScreenCard>
    );
};

export default MissionLogsScreen;
